
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../services/firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  deleteDoc, 
  doc, 
  limit 
} from 'firebase/firestore';
import { UserRole, UserProfile } from '../types';

interface Message {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  senderPhoto: string;
  senderRole: UserRole;
  group: string;
  createdAt: number;
  isOptimistic?: boolean;
}

const ChatSystem: React.FC<{ user: UserProfile }> = ({ user }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [activeGroup, setActiveGroup] = useState<'general' | string>('general');
  const [classes, setClasses] = useState<any[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Natural Sorting Helper for Channel List
  const sortNaturally = (list: any[]) => {
    return [...list].sort((a, b) => {
      const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0');
      const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0');
      if (aNum !== bNum) return bNum - aNum; // Descending: 12, 11, 10...
      return a.name.localeCompare(b.name);
    });
  };

  useEffect(() => {
    if (user.role !== UserRole.STUDENT) {
      const unsubscribe = onSnapshot(collection(db, 'classes'), (snap) => {
        const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setClasses(sortNaturally(raw));
      });
      return () => unsubscribe();
    }
  }, [user]);

  // Real-time listener: 
  // To avoid "The query requires an index" error, we remove orderBy from the query 
  // and sort the results manually on the client side.
  useEffect(() => {
    const q = query(
      collection(db, 'chats'),
      where('group', '==', activeGroup),
      limit(150) // Increased limit to ensure enough history is fetched before client-sort
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(d => ({ 
        id: d.id, 
        ...d.data()
      } as Message));
      
      // Client-side Sort: Essential to fix the Firebase composite index error
      msgs.sort((a, b) => a.createdAt - b.createdAt);
      
      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [activeGroup]);

  // Smooth scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = inputText.trim();
    if (!text) return;

    // We use Date.now() for the timestamp to ensure it's available 
    // immediately for local sorting before the server syncs.
    const msgData = {
      text,
      senderId: user.uid,
      senderName: user.displayName,
      senderPhoto: user.photoURL,
      senderRole: user.role,
      group: activeGroup,
      createdAt: Date.now() 
    };

    setInputText('');

    try {
      await addDoc(collection(db, 'chats'), msgData);
    } catch (err) {
      console.error("Message failed to send", err);
      alert("Transmission failed. Re-check connection.");
    }
  };

  const handleDeleteMessage = async (msg: Message) => {
    let allowed = user.uid === msg.senderId || user.role === UserRole.ADMIN || (user.role === UserRole.STAFF && msg.senderRole === UserRole.STUDENT);
    if (allowed && confirm("Delete message?")) {
      await deleteDoc(doc(db, 'chats', msg.id));
    }
  };

  const getActiveTitle = () => {
    if (activeGroup === 'general') return 'Community Hub';
    const classObj = classes.find(c => c.id === activeGroup);
    return classObj ? classObj.name : (user.classId === activeGroup ? 'My Classroom' : 'Channel');
  };

  return (
    <div className="flex h-[calc(100vh-180px)] bg-white dark:bg-dark-card rounded-[2.5rem] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden transition-all duration-500">
      
      {/* Dynamic Glass Sidebar */}
      <div className={`fixed lg:relative inset-y-0 left-0 z-40 w-72 bg-gray-50/80 dark:bg-dark-sidebar/40 backdrop-blur-2xl border-r border-gray-100 dark:border-gray-800 transition-transform duration-300 lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-8">
            <div className="flex items-center gap-2 mb-8">
                <div className="w-1.5 h-6 bg-purple-600 rounded-full"></div>
                <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Live Channels</h3>
            </div>
            
            <div className="space-y-2">
                <button 
                    onClick={() => { setActiveGroup('general'); setIsSidebarOpen(false); }}
                    className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl transition-all ${activeGroup === 'general' ? 'bg-purple-600 text-white shadow-xl shadow-purple-500/20' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-800/50'}`}
                >
                    <i className="fas fa-globe-asia"></i>
                    <span className="font-black text-sm">General</span>
                </button>
                
                <div className="my-6 border-t border-gray-100 dark:border-gray-800"></div>

                {user.role === UserRole.STUDENT && user.classId && (
                    <button 
                      onClick={() => { setActiveGroup(user.classId!); setIsSidebarOpen(false); }}
                      className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl transition-all ${activeGroup === user.classId ? 'bg-purple-600 text-white shadow-xl shadow-purple-500/20' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-800/50'}`}
                    >
                        <i className="fas fa-graduation-cap"></i>
                        <span className="font-black text-sm">My Class</span>
                    </button>
                )}
                
                {user.role !== UserRole.STUDENT && classes.map(c => (
                    <button 
                        key={c.id}
                        onClick={() => { setActiveGroup(c.id); setIsSidebarOpen(false); }}
                        className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl transition-all ${activeGroup === c.id ? 'bg-purple-600 text-white shadow-xl shadow-purple-500/20' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-800/50'}`}
                    >
                        <i className="fas fa-book-bookmark text-xs"></i>
                        <span className="font-black text-sm">{c.name}</span>
                    </button>
                ))}
            </div>
        </div>
      </div>

      {/* Primary Chat Window */}
      <div className="flex-1 flex flex-col min-w-0 bg-white/50 dark:bg-dark-card/50 backdrop-blur-sm">
        
        {/* Header */}
        <header className="px-8 py-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-white dark:bg-dark-card z-30">
            <div className="flex items-center gap-4">
                <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <i className="fas fa-indent text-gray-500"></i>
                </button>
                <div>
                    <h2 className="text-xl font-black dark:text-white tracking-tight flex items-center gap-2">
                        <span className="text-purple-600">#</span> {getActiveTitle()}
                    </h2>
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">Active Academic Session</p>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <div className="hidden sm:flex px-4 py-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 text-[10px] font-black rounded-full uppercase tracking-widest border border-purple-100 dark:border-purple-800">
                    {messages.length} Messages
                </div>
            </div>
        </header>

        {/* Message Feed */}
        <div 
          ref={scrollRef} 
          className="flex-1 overflow-y-auto p-6 lg:p-10 space-y-8 no-scrollbar bg-gray-50/20 dark:bg-dark-bg/20"
        >
            {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center opacity-20 scale-90">
                    <div className="w-24 h-24 bg-gray-300 dark:bg-gray-700 rounded-[2.5rem] flex items-center justify-center mb-6">
                        <i className="fas fa-comment-dots text-4xl"></i>
                    </div>
                    <p className="text-sm font-black uppercase tracking-[0.3em]">Channel Start</p>
                </div>
            )}

            {messages.map((msg, idx) => {
                const isMe = msg.senderId === user.uid;
                const showAvatar = idx === 0 || messages[idx-1].senderId !== msg.senderId;

                return (
                    <div 
                        key={msg.id} 
                        className={`flex gap-4 group animate-slideIn ${isMe ? 'flex-row-reverse' : 'flex-row'} ${!showAvatar ? 'mt-[-1.5rem]' : ''}`}
                    >
                        {showAvatar ? (
                            <img src={msg.senderPhoto} className="w-10 h-10 rounded-2xl object-cover ring-2 ring-white dark:ring-gray-800 shadow-md self-end" alt="" />
                        ) : (
                            <div className="w-10"></div>
                        )}

                        <div className={`flex flex-col max-w-[85%] lg:max-w-[70%] ${isMe ? 'items-end' : 'items-start'}`}>
                            {showAvatar && (
                                <div className={`flex items-center gap-2 mb-2 px-1 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                    <span className="text-[11px] font-black dark:text-white">{msg.senderName}</span>
                                    <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-lg ${
                                        msg.senderRole === UserRole.ADMIN ? 'bg-amber-500 text-white' : 
                                        msg.senderRole === UserRole.STAFF ? 'bg-blue-600 text-white' : 
                                        'bg-gray-100 dark:bg-gray-800 text-gray-400'
                                    }`}>
                                        {msg.senderRole}
                                    </span>
                                </div>
                            )}

                            <div className="relative group/bubble">
                                <div className={`px-6 py-4 text-sm shadow-sm transition-all duration-300 ${
                                    isMe 
                                    ? 'bg-purple-600 text-white rounded-[2rem] rounded-tr-none' 
                                    : 'bg-white dark:bg-gray-800 dark:text-white rounded-[2rem] rounded-tl-none border border-gray-100 dark:border-gray-700'
                                }`}>
                                    {msg.text}
                                </div>
                                
                                {(user.uid === msg.senderId || user.role === UserRole.ADMIN || (user.role === UserRole.STAFF && msg.senderRole === UserRole.STUDENT)) && (
                                    <button 
                                        onClick={() => handleDeleteMessage(msg)}
                                        className={`absolute top-1/2 -translate-y-1/2 p-2.5 bg-red-500 text-white rounded-full opacity-0 group-hover/bubble:opacity-100 transition-all hover:scale-110 shadow-lg z-10 ${isMe ? '-left-12' : '-right-12'}`}
                                    >
                                        <i className="fas fa-trash-alt text-[10px]"></i>
                                    </button>
                                )}
                            </div>
                            
                            <p className="text-[8px] text-gray-400 mt-2 font-bold uppercase tracking-widest opacity-60">
                                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>

        {/* Composer */}
        <div className="p-8 bg-white dark:bg-dark-card border-t border-gray-100 dark:border-gray-800">
            <form onSubmit={handleSendMessage} className="relative">
                <input 
                    type="text" 
                    placeholder="Enter your message..."
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-gray-800/50 pl-8 pr-24 py-6 rounded-[2rem] dark:text-white outline-none border border-transparent focus:border-purple-500 transition-all shadow-inner text-sm font-medium"
                />
                <button 
                    type="submit"
                    disabled={!inputText.trim()}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-14 h-14 bg-purple-600 text-white rounded-2xl shadow-xl hover:bg-purple-700 disabled:opacity-20 transition-all flex items-center justify-center active:scale-95"
                >
                    <i className="fas fa-paper-plane"></i>
                </button>
            </form>
            <div className="flex items-center justify-center gap-6 mt-6">
                <div className="flex items-center gap-2 opacity-30">
                    <i className="fas fa-shield-halved text-[10px]"></i>
                    <span className="text-[9px] font-black uppercase tracking-widest">Encrypted</span>
                </div>
                <div className="flex items-center gap-2 opacity-30">
                    <i className="fas fa-bolt text-[10px]"></i>
                    <span className="text-[9px] font-black uppercase tracking-widest">Real-time</span>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSystem;
